import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { EMPTY_VALUE } from "@/i18n/format";
import { countryOptions, regionOptions } from "@/lib/regionOptions";
import { formatScore, hasTrustBadge, trustBadgeLabel } from "@/lib/trustLabels";
import type { LeadSource, ModeratorCompany, ModeratorTag } from "@/services/moderators/generated/model";
import {
  getModeratorsCompaniesListQueryKey,
  useModeratorsCompaniesList,
  // readiness band filter is a client-side intersection — see below.
  useModeratorsCompaniesSetTagsCreate,
  useModeratorsLeadSourcesList,
  useModeratorsTagsList,
} from "@/services/moderators/generated/moderators-companies/moderators-companies";
import { useModeratorsReadinessCompaniesList } from "@/services/moderators/generated/moderators-readiness/moderators-readiness";
import SourceBreakdownCard, { sourceTypeFilters } from "./SourceBreakdownCard";
import "./ModeratorPage.css";
import "./ModeratorCompaniesPage.css";

const ModeratorCompaniesPage = () => (
  <ModeratorLayout>
    <CompaniesContent />
  </ModeratorLayout>
);

const CompaniesContent = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  // Lead source filters (WP1.9).
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [leadSourceFilter, setLeadSourceFilter] = useState("");
  // WP2.1 readiness band filter (client-side intersect).
  const [bandFilter, setBandFilter] = useState("");
  const [editingCompany, setEditingCompany] = useState<ModeratorCompany | null>(null);

  const queryParams = useMemo(() => {
    const params: {
      search?: string;
      tag?: string;
      country?: string;
      region?: string;
      sourceType?: string;
      leadSource?: string;
    } = {};
    if (search.trim()) params.search = search.trim();
    if (tagFilter.trim()) params.tag = tagFilter.trim();
    if (countryFilter) params.country = countryFilter;
    if (regionFilter) params.region = regionFilter;
    if (sourceTypeFilter) params.sourceType = sourceTypeFilter;
    if (leadSourceFilter) params.leadSource = leadSourceFilter;
    return params;
  }, [search, tagFilter, countryFilter, regionFilter, sourceTypeFilter, leadSourceFilter]);

  const { data: companiesData, isLoading, error } = useModeratorsCompaniesList(queryParams);

  // Readiness band filter is applied client-side by intersecting the
  // company list with the moderator readiness roster.
  const readinessParams = bandFilter ? { band: bandFilter } : undefined;
  const { data: readinessRoster = [] } = useModeratorsReadinessCompaniesList(readinessParams);
  const bandIds = useMemo(() => {
    if (!bandFilter) return null;
    const rows = readinessRoster as { companyId: number }[];
    return new Set(rows.map((r) => r.companyId));
  }, [bandFilter, readinessRoster]);

  const { data: tagsData } = useModeratorsTagsList();
  const allTags = unwrapPaginated<ModeratorTag>(tagsData);
  const rawCompanies = unwrapPaginated<ModeratorCompany>(companiesData);
  const companies = bandIds ? rawCompanies.filter((c) => bandIds.has(c.id as number)) : rawCompanies;
  const { data: leadSourcesData } = useModeratorsLeadSourcesList({ page_size: 100 });
  const leadSources = unwrapPaginated<LeadSource>(leadSourcesData);

  const setTagsMutation = useModeratorsCompaniesSetTagsCreate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getModeratorsCompaniesListQueryKey(queryParams) });
        setEditingCompany(null);
      },
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleSaveTags = (companyId: number, selectedTags: string[]) => {
    setTagsMutation.mutate({ id: companyId, data: { tags: selectedTags } });
  };

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("companies.title")}</h1>
          <p className="mod-page__subtitle">{t("companies.subtitle")}</p>
        </div>
      </header>

      <SourceBreakdownCard
        onPickType={(type) => {
          setSourceTypeFilter(type);
          setLeadSourceFilter("");
        }}
        onPickSource={(key) => {
          setLeadSourceFilter(key);
          setSourceTypeFilter("");
        }}
      />

      <form className="mod-page__toolbar" onSubmit={handleSearch}>
        <input
          type="search"
          className="mod-page__input"
          placeholder={t("companies.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select className="mod-page__select" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
          <option value="">{t("companies.allTags")}</option>
          {allTags.map((tag) => (
            <option key={tag.id} value={tag.name}>
              {tag.name}
            </option>
          ))}
        </select>
        <select className="mod-page__select" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
          <option value="">{t("companies.allRegions")}</option>
          {regionOptions().map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select className="mod-page__select" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
          <option value="">{t("companies.allCountries")}</option>
          {countryOptions().map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          className="mod-page__select"
          value={sourceTypeFilter}
          onChange={(e) => setSourceTypeFilter(e.target.value)}
          aria-label={t("companies.filterBySourceType")}
        >
          <option value="">{t("companies.allSourceTypes")}</option>
          {sourceTypeFilters().map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          className="mod-page__select"
          value={leadSourceFilter}
          onChange={(e) => setLeadSourceFilter(e.target.value)}
          aria-label={t("companies.filterByNamedSource")}
        >
          <option value="">{t("companies.allNamedSources")}</option>
          {leadSources.map((src) => (
            <option key={src.id} value={String(src.id)}>
              {src.isActive === false ? t("companies.inactiveSource", { name: src.name }) : src.name}
            </option>
          ))}
          <option value="unlisted">{t("companies.notListed")}</option>
          <option value="unknown">{t("common:states.unknown")}</option>
        </select>
        <select
          className="mod-page__select"
          value={bandFilter}
          onChange={(e) => setBandFilter(e.target.value)}
          aria-label={t("readiness.companies.filterLabel")}
        >
          <option value="">
            {t("readiness.companies.all")} — {t("readiness.companies.filterLabel")}
          </option>
          <option value="ready">{t("readiness.bands.ready")}</option>
          <option value="developing">{t("readiness.bands.developing")}</option>
          <option value="not_ready">{t("readiness.bands.not_ready")}</option>
          <option value="none">{t("readiness.companies.none")}</option>
        </select>
        <button type="submit" className="mod-page__btn mod-page__btn--primary">
          {t("common:actions.apply")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          onClick={() => {
            setSearchInput("");
            setSearch("");
            setTagFilter("");
            setCountryFilter("");
            setRegionFilter("");
            setSourceTypeFilter("");
            setLeadSourceFilter("");
            setBandFilter("");
          }}
        >
          {t("companies.reset")}
        </button>
      </form>

      {isLoading ? <div className="mod-page__loading">{t("companies.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("companies.loadError")}</div> : null}

      {!isLoading && companies.length === 0 ? <div className="mod-page__empty">{t("companies.empty")}</div> : null}

      {companies.map((company) => (
        <CompanyRow
          key={company.id}
          company={company}
          allTags={allTags}
          isEditing={editingCompany?.id === company.id}
          onStartEdit={() => setEditingCompany(company)}
          onCancelEdit={() => setEditingCompany(null)}
          onSave={(tags) => handleSaveTags(company.id as number, tags)}
          isSaving={setTagsMutation.isPending && setTagsMutation.variables?.id === company.id}
        />
      ))}
    </div>
  );
};

interface CompanyRowProps {
  company: ModeratorCompany;
  allTags: ModeratorTag[];
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (tags: string[]) => void;
  isSaving: boolean;
}

const CompanyRow = ({ company, allTags, isEditing, onStartEdit, onCancelEdit, onSave, isSaving }: CompanyRowProps) => {
  const { t } = useTranslation(["moderator", "common"]);
  const initial = (company.tags ?? []) as unknown as string[];
  const [selected, setSelected] = useState<Set<string>>(new Set(initial));

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <article className="mod-page__card mod-companies__card">
      <div className="mod-page__card-body">
        <h2 className="mod-page__card-title">{company.companyName}</h2>
        <p className="mod-page__card-meta">
          {company.companyEmail}
          {company.companyIndustry ? ` · ${company.companyIndustry}` : ""}
        </p>
        <p className="mod-page__card-meta">
          <span
            className={`mod-page__chip ${company.isApproved ? "mod-page__chip--success" : "mod-page__chip--warning"}`}
          >
            {company.isApproved ? t("shared.approved") : t("shared.pending")}
          </span>
          <span className={`mod-page__chip ${company.isActive ? "mod-page__chip--success" : "mod-page__chip--danger"}`}>
            {company.isActive ? t("shared.active") : t("shared.inactive")}
          </span>
          <span className="mod-page__chip">
            {t("companies.compliance", {
              status: company.overallComplianceStatus ?? EMPTY_VALUE,
              score: formatScore(company.overallComplianceScore),
            })}
          </span>
          <span className={`mod-page__chip ${hasTrustBadge(company) ? "mod-page__chip--success" : ""}`}>
            {t("companies.badge", { label: trustBadgeLabel(company) })}
          </span>
          <span className="mod-page__chip">
            {t("companies.source", { label: company.leadSourceLabel || t("common:states.unknown") })}
          </span>
        </p>

        {!isEditing ? (
          <p className="mod-page__card-meta mod-companies__current-tags">
            {t("companies.tagsLabel")}{" "}
            {initial.length > 0
              ? initial.map((tag) => (
                  <span key={tag} className="mod-page__chip">
                    {tag}
                  </span>
                ))
              : EMPTY_VALUE}
          </p>
        ) : (
          <div className="mod-companies__tag-grid">
            {allTags.length === 0 ? (
              <span className="mod-page__card-meta">{t("companies.noTags")}</span>
            ) : (
              allTags.map((tag) => (
                <label key={tag.id} className="mod-companies__tag-option">
                  <input type="checkbox" checked={selected.has(tag.name)} onChange={() => toggle(tag.name)} />
                  <span>{tag.name}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>

      <div className="mod-companies__actions">
        <Link to={`/moderator/companies/${company.id}`} className="mod-page__btn mod-page__btn--primary">
          {t("companies.open")}
        </Link>
        {!isEditing ? (
          <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onStartEdit}>
            {t("companies.editTags")}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="mod-page__btn mod-page__btn--primary"
              onClick={() => onSave(Array.from(selected))}
              disabled={isSaving}
            >
              {isSaving ? t("common:actions.saving") : t("common:actions.save")}
            </button>
            <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onCancelEdit}>
              {t("common:actions.cancel")}
            </button>
          </>
        )}
      </div>
    </article>
  );
};

export default ModeratorCompaniesPage;
